import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { AiSparkleIcon } from "@/components/ai-suggestion/ai-sparkle-icon";

/**
 * Tint applied to any control whose current value is in suggested state —
 * pass to the control's own className so Input, Textarea, SelectTrigger,
 * and container-style controls all read as suggested the same way.
 */
export const AI_SUGGESTED_CONTROL_CLASSES =
  "border-blue-300 bg-blue-100 focus-visible:ring-blue-300";

export interface AiSuggestedDismissButtonProps {
  /** Label of the suggested field, used to build the accessible name. */
  fieldLabel: string;
  onDismiss: () => void;
  className?: string;
}

/** Small X button that rejects a suggested value (empties the field). */
export function AiSuggestedDismissButton({
  fieldLabel,
  onDismiss,
  className,
}: AiSuggestedDismissButtonProps) {
  return (
    <button
      type="button"
      aria-label={`Dismiss autofilled value for ${fieldLabel}`}
      onClick={onDismiss}
      // Padding grows the tap target (the icon alone is far below the 24px
      // WCAG minimum for an irreversible action); callers in flex flow
      // compensate with a negative margin. blue-600 (not blue-500, which
      // computes ~3.0:1 on the blue-100 chip) keeps the glyph safely above
      // the 3:1 WCAG 1.4.11 non-text contrast minimum.
      className={cn(
        "shrink-0 rounded-full p-1.5 text-blue-600 transition-colors hover:bg-blue-200 hover:text-blue-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className,
      )}
    >
      <X size={14} />
    </button>
  );
}

export interface AiSuggestedMarkerProps {
  /** Label of the suggested field, used to build accessible names. */
  fieldLabel: string;
  onDismiss: () => void;
  /**
   * id of the control this chip decorates. When set, dismissing focuses the
   * control synchronously so focus lands there instead of falling back to
   * <body> once the chip (and its X) unmounts.
   */
  controlId?: string;
  /** id for the chip's visible text, target of the control's aria-describedby. */
  descriptionId?: string;
  className?: string;
}

/**
 * Compact pill (sparkle + "Autofilled with AI" + X) rendered in a field's
 * label row while its value is in suggested state.
 */
export function AiSuggestedMarker({
  fieldLabel,
  onDismiss,
  controlId,
  descriptionId,
  className,
}: AiSuggestedMarkerProps) {
  const handleDismiss = () => {
    onDismiss();
    if (controlId) {
      document.getElementById(controlId)?.focus();
    }
  };

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full border border-blue-200 bg-blue-100 py-0.5 pl-2 pr-0.5",
        className,
      )}
    >
      <AiSparkleIcon size={12} />
      <span
        id={descriptionId}
        className="text-xs font-medium text-blue-700 whitespace-nowrap"
      >
        Autofilled with AI
      </span>
      <AiSuggestedDismissButton
        fieldLabel={fieldLabel}
        onDismiss={handleDismiss}
        className="-m-1.5"
      />
    </span>
  );
}
